import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle, ArrowRight, CheckCircle2, FileText, Loader2, Pencil, Plus, Upload,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface PreviewEntry {
  row: number;
  action: "create" | "update" | "skip";
  data: Record<string, unknown>;
  existing?: Record<string, unknown>;
  warning?: string;
}

interface ImportSummary {
  dry_run:      boolean;
  would_create: number;
  would_update: number;
  created:      number;
  updated:      number;
  errors:       { row: number; message: string }[];
  preview:      PreviewEntry[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title shown at the top of the dialog. */
  title: string;
  /** Plain-language description of what the dialog imports. */
  description: string;
  /** Text shown above the file-picker explaining the expected columns. */
  columnsHint: React.ReactNode;
  /** Target endpoint — POST {file, dry_run?} → ImportSummary JSON. */
  endpoint: string;
  /** React Query keys to invalidate on successful commit. */
  invalidateKeys?: (string | number)[][];
}

type Stage = "pick" | "review" | "done";

/**
 * Two-step CSV import:
 *
 *   1. PICK    — user chooses a CSV; we POST it with `dry_run=true` to
 *                fetch a preview. Nothing is written yet.
 *   2. REVIEW  — show per-row plan ("3 will be created · 2 updated · 1
 *                skipped"), per-row data, warnings (e.g. date-snap notes),
 *                and any parsing errors. User clicks Confirm to commit.
 *   3. DONE    — summary of what actually persisted; caches invalidated.
 *
 * Either step has a "Back" path. Cancel mid-flight is disabled.
 */
export function ImportCsvDialog({
  open, onOpenChange, title, description, columnsHint, endpoint, invalidateKeys,
}: Props) {
  const qc = useQueryClient();
  const [stage,    setStage]   = useState<Stage>("pick");
  const [file,     setFile]    = useState<File | null>(null);
  const [busy,     setBusy]    = useState(false);
  const [error,    setError]   = useState<string | null>(null);
  const [preview,  setPreview] = useState<ImportSummary | null>(null);
  const [committed, setCommitted] = useState<ImportSummary | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setStage("pick"); setFile(null); setBusy(false);
    setError(null); setPreview(null); setCommitted(null);
  }, [open]);

  async function runDryRun() {
    if (!file || busy) return;
    setBusy(true); setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<ImportSummary>(
        `${endpoint}?dry_run=true`, form,
      );
      setPreview(data);
      setStage("review");
    } catch (err) {
      setError(extractError(err));
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!file || busy) return;
    setBusy(true); setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<ImportSummary>(endpoint, form);
      setCommitted(data);
      setStage("done");
      for (const key of invalidateKeys ?? []) {
        qc.invalidateQueries({ queryKey: key });
      }
    } catch (err) {
      setError(extractError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            <span className="text-xs font-normal text-muted-foreground">
              · Step {stage === "pick" ? "1 of 2" : stage === "review" ? "2 of 2" : "done"}
            </span>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {stage === "pick" && (
          <PickStep
            file={file} setFile={setFile} fileInputRef={fileInputRef}
            columnsHint={columnsHint} error={error} busy={busy}
          />
        )}

        {stage === "review" && preview && (
          <ReviewStep summary={preview} />
        )}

        {stage === "done" && committed && (
          <DoneStep summary={committed} />
        )}

        <DialogFooter className="pt-2">
          {stage === "pick" && (
            <>
              <Button type="button" variant="ghost"
                onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="button" disabled={!file || busy} onClick={runDryRun}>
                {busy
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reading…</>
                  : <>Preview<ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </>
          )}

          {stage === "review" && preview && (
            <>
              <Button type="button" variant="ghost"
                onClick={() => { setStage("pick"); setPreview(null); }}
                disabled={busy}>
                Back
              </Button>
              <Button type="button" disabled={busy} onClick={commit}>
                {busy
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Committing…</>
                  : <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Confirm — apply {preview.would_create + preview.would_update} change
                      {preview.would_create + preview.would_update === 1 ? "" : "s"}
                    </>}
              </Button>
            </>
          )}

          {stage === "done" && (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — file picker
// ---------------------------------------------------------------------------
function PickStep({
  file, setFile, fileInputRef, columnsHint, error, busy,
}: {
  file: File | null;
  setFile: (f: File | null) => void;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  columnsHint: React.ReactNode;
  error: string | null;
  busy: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
        {columnsHint}
      </div>

      {file ? (
        <div className="flex items-center gap-3 rounded-md border bg-card p-2.5">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
          <Button type="button" variant="ghost" size="sm"
            onClick={() => setFile(null)} disabled={busy}>
            Change
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" className="w-full"
          onClick={() => fileInputRef.current?.click()} disabled={busy}>
          <Upload className="mr-2 h-4 w-4" />Pick a CSV file
        </Button>
      )}
      <input
        ref={fileInputRef} type="file" accept=".csv,text/csv" hidden
        onChange={(e) => { setFile(e.target.files?.[0] ?? null); e.target.value = ""; }}
      />

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — review the parsed plan
// ---------------------------------------------------------------------------
function ReviewStep({ summary }: { summary: ImportSummary }) {
  return (
    <div className="space-y-3">
      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Chip tone="emerald" icon={<Plus className="h-3 w-3" />}>
          {summary.would_create} new
        </Chip>
        <Chip tone="blue" icon={<Pencil className="h-3 w-3" />}>
          {summary.would_update} update{summary.would_update === 1 ? "" : "s"}
        </Chip>
        {summary.errors.length > 0 && (
          <Chip tone="amber" icon={<AlertCircle className="h-3 w-3" />}>
            {summary.errors.length} skipped
          </Chip>
        )}
        <span className="ml-auto text-muted-foreground">
          Nothing has been written yet.
        </span>
      </div>

      {/* Errors */}
      {summary.errors.length > 0 && (
        <Card className="border-amber-300/60 bg-amber-50/40 p-3 text-xs dark:border-amber-500/30 dark:bg-amber-500/5">
          <div className="mb-1 font-semibold text-amber-700 dark:text-amber-300">
            Rows the importer will skip
          </div>
          <ul className="space-y-0.5 text-foreground/80">
            {summary.errors.slice(0, 8).map((e, i) => (
              <li key={i}>
                <span className="font-mono text-muted-foreground">row {e.row}:</span> {e.message}
              </li>
            ))}
            {summary.errors.length > 8 && (
              <li className="text-muted-foreground">… and {summary.errors.length - 8} more</li>
            )}
          </ul>
        </Card>
      )}

      {/* Per-row plan */}
      <Card className="max-h-72 overflow-y-auto p-0">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/40 text-left">
            <tr>
              <th className="w-12 px-2 py-1.5 font-medium text-muted-foreground">Row</th>
              <th className="w-20 px-2 py-1.5 font-medium text-muted-foreground">Action</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Data</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {summary.preview.length === 0 ? (
              <tr><td colSpan={3} className="px-2 py-6 text-center text-muted-foreground">
                Nothing to import. All rows were skipped — see above.
              </td></tr>
            ) : summary.preview.map((p) => (
              <tr key={p.row} className="align-top">
                <td className="px-2 py-1.5 font-mono text-muted-foreground">{p.row}</td>
                <td className="px-2 py-1.5">
                  {p.action === "create" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                      <Plus className="h-2.5 w-2.5" />new
                    </span>
                  )}
                  {p.action === "update" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                      <Pencil className="h-2.5 w-2.5" />update
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <DataCell entry={p} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function DataCell({ entry }: { entry: PreviewEntry }) {
  // Render a compact "k: v" inline list with optional warning + diff
  // hint when this row would update an existing record.
  const items = Object.entries(entry.data).slice(0, 8);
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {items.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className="text-muted-foreground">{k}:</span>
            <span className="font-medium">{String(v ?? "—")}</span>
          </span>
        ))}
      </div>
      {entry.warning && (
        <div className="text-amber-700 dark:text-amber-300">
          ⚠ {entry.warning}
        </div>
      )}
      {entry.existing && (
        <div className="text-muted-foreground">
          replaces:{" "}
          {Object.entries(entry.existing).slice(0, 4).map(([k, v]) => (
            <span key={k} className="mr-2">
              <span>{k}:</span> <span className="font-mono">{String(v ?? "—")}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — done
// ---------------------------------------------------------------------------
function DoneStep({ summary }: { summary: ImportSummary }) {
  const total = summary.created + summary.updated;
  return (
    <div className={cn(
      "space-y-2 rounded-md border p-4 text-sm",
      summary.errors.length === 0
        ? "border-emerald-300/60 bg-emerald-50/50 dark:border-emerald-500/30 dark:bg-emerald-500/5"
        : "border-amber-300/60 bg-amber-50/50 dark:border-amber-500/30 dark:bg-amber-500/5",
    )}>
      <div className="flex items-center gap-2 font-semibold">
        <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
        Import complete — {total} row{total === 1 ? "" : "s"} written.
      </div>
      <div className="text-xs text-muted-foreground">
        {summary.created} new · {summary.updated} updated
        {summary.errors.length ? ` · ${summary.errors.length} skipped` : ""}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Chip({
  tone, icon, children,
}: {
  tone: "emerald" | "blue" | "amber";
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls = {
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    blue:    "bg-blue-100    text-blue-700    dark:bg-blue-500/20    dark:text-blue-300",
    amber:   "bg-amber-100   text-amber-700   dark:bg-amber-500/20   dark:text-amber-300",
  }[tone];
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium", cls)}>
      {icon}{children}
    </span>
  );
}

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: string } } };
  return e?.response?.data?.detail || "Request failed.";
}
